import React from 'react';
import FormLabel from 'ui/FormLabel';
import { bool, node, string, oneOfType, object } from 'prop-types';

import { colors } from 'css/constants';

import styled from 'styled-components/macro';

const InputWrapperPrimitive = styled.div`
  display: flex;
  flex-direction: row;
  flex-direction: ${({ direction = 'column' }) => direction};
  justify-content: flex-end;
  ${({ fullWidth }) => fullWidth && 'grid-column: 1/3;'}
  margin: 6px 0;
  padding-bottom: ${({ error, noPadding }) =>
    error || noPadding ? '0' : '20px'};
`;

export const NoteLabel = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  line-height: 1.8;
  opacity: 0.8;
  font-size: 0.8rem;
  color: ${({ lightNote }) => (lightNote ? colors.sohoBlue : colors.sohoGray)};
  font-weight: 300;
  margin-top: 4px;
  margin-left: 0;
  font-style: italic;
`;

export const ErrorLabel = styled(NoteLabel)`
  color: ${colors.red};
  margin-left: ${({ version }) => (version === 'v1' ? '6px' : '0px')};
  font-weight: 400;
  font-style: normal;
`;

const ErrorLabelNext = styled(ErrorLabel)`
  margin: 0;
`;

/** Adds a label to the wrapped input */
const InputWrapper = ({
  children,
  className,
  error,
  fullWidth,
  label,
  name,
  withMarginBottom,
  disabled,
  direction,
  noPadding,
  version,
  testId,
  emptyError,
  note,
  lightNote
}) => (
  <InputWrapperPrimitive
    className={className}
    fullWidth={fullWidth}
    withMarginBottom={withMarginBottom}
    disabled={disabled}
    direction={direction}
    version={version}
    error={error}
    data-testid={testId}
    noPadding={noPadding}
  >
    <FormLabel disabled={disabled} htmlFor={name} error={!!error || emptyError}>
      {label}
      {error && version === 'v1' && (
        <ErrorLabel data-testid="error">{error}</ErrorLabel>
      )}
    </FormLabel>
    {children}
    {error && version === 'v2' && (
      <ErrorLabelNext data-testid="error">{error}</ErrorLabelNext>
    )}
    {note && (
      <NoteLabel data-testid="note" lightNote={lightNote}>
        {note}
      </NoteLabel>
    )}
  </InputWrapperPrimitive>
);

InputWrapper.propTypes = {
  /** The form input to be wrapped */
  children: node.isRequired,
  /** Custom styles applied to the element */
  className: string,
  /** String to display in case of error */
  error: string,
  /** Whether it should take the entire width of the form or only half */
  fullWidth: bool,
  /** Text of the label */
  label: oneOfType([string, object]).isRequired,
  /** Name of the form element */
  name: string.isRequired,
  /** Adds a margin to the bottom of the wrapper */
  withMarginBottom: bool,
  disabled: bool,
  direction: string,
  version: string,
  testId: string,
  noPadding: bool,
  emptyError: bool,
  note: string,
  lightNote: bool
};

InputWrapper.defaultProps = {
  className: '',
  direction: 'column',
  version: 'v1',
  noPadding: false,
  emptyError: false,
  note: '',
  lightNote: false
};

export default InputWrapper;
